import { useState } from 'react';

import { CloudUpload } from '@mui/icons-material';
import { Box, Typography, Button, CircularProgress, Paper, Alert } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';

import {
  client,
  getMissingDataApiV1MissingDataDataTypeGet,
  getUploadErrorsApiV1UploadsUploadIdErrorsGet,
  getUploadStatusApiV1UploadsUploadIdGet, uploadDataApiV1UploadsDataTypePost,
} from '../../client';
import { useAuth } from '../../services/auth/AuthContext';
import NotificationService from '../../services/notification/NotificationService';
import PageLayout from '../PageLayout/PageLayout';
import { PageId } from '../Sidebar/Sidebar';

export enum UploadType {
  Transactions = 'transactions',
  CreditReport = 'credit_report',
}

interface UploadPageProps {
  title: string;
  uploadType: UploadType;
  showDateRange?: boolean;
  pageId: PageId;
}

export const UploadPage = ({ title, uploadType, showDateRange, pageId }: UploadPageProps) => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [errors, setErrors] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFile(acceptedFiles[0]);
    },
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleUpload = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);
    setUploadStatus('Uploading...');
    setErrors([]);

    try {
      const token = await getToken();
      client.setConfig({
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!startDate || !endDate) {
        NotificationService.error('Please select both start and end dates');
        return;
      }
      const response = await uploadDataApiV1UploadsDataTypePost({
        body: { file },
        path: {
          data_type: uploadType,
        },
        query: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
        },
      });

      if (response?.data) {
        setUploadStatus('Upload successful! Processing...');
        pollUploadStatus(response.data.upload_id);
      }

      if (response?.error) {
        NotificationService.error('Upload failed: ' + response.error.detail, 5000);
        setUploadStatus('Upload failed');
      }
    } catch (error) {
      NotificationService.error('Upload failed');
      setUploadStatus('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const checkForMissingData = async () => {
    try {
      // Check for missing product group data
      const productGroupResponse = await getMissingDataApiV1MissingDataDataTypeGet({
        path: { data_type: 'product_group' },
      });

      // Check for missing buyer by product data
      const buyerByProductResponse = await getMissingDataApiV1MissingDataDataTypeGet({
        path: { data_type: 'buyer_by_product' },
      });

      const productGroupMissingItems = productGroupResponse.data?.missing_items || [];
      const buyerByProductMissingItems = buyerByProductResponse.data?.missing_items || [];

      // If there are any missing items, notify user and redirect
      if (productGroupMissingItems.length > 0 || buyerByProductMissingItems.length > 0) {
        NotificationService.warning('Data issues detected - redirecting to data issues page');

        // Redirect after 5 seconds
        setTimeout(() => {
          navigate('/issues/data');
        }, 5000);
      }
    } catch (error) {
      NotificationService.error('Error checking for missing data: ' + error);
    }
  };

  const pollUploadStatus = async (uploadId: number) => {
    try {
      const response = await getUploadStatusApiV1UploadsUploadIdGet({
        path: { upload_id: uploadId },
      });

      if (response.data && response.data.status === 'completed') {
        setUploadStatus('Processing complete');
        if (response.data.error_count && response.data.error_count > 0) {
          fetchUploadErrors(uploadId);
        } else {
          NotificationService.success(`Successfully uploaded ${response.data.success_count} rows`);
          // Check for missing data after successful upload
          checkForMissingData();
        }
      } else {
        setTimeout(() => pollUploadStatus(uploadId), 5000);
      }
    } catch (error) {
      NotificationService.error('Error polling upload status: ' + error, 5000);
    }
  };

  const fetchUploadErrors = async (uploadId: number) => {
    try {
      const response = await getUploadErrorsApiV1UploadsUploadIdErrorsGet({
        path: { upload_id: uploadId },
      });
      if (response.data && response.data.errors) {
        setErrors(response.data.errors);
      }
    } catch (error) {
      NotificationService.error('Error fetching upload errors');
    }
  };

  return (
    <PageLayout pageId={pageId}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>

        {showDateRange && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={startDate || undefined}
                onChange={(newValue) => setStartDate(newValue)}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                minDate={startDate || undefined}
              />
            </Box>
          </LocalizationProvider>
        )}

        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            textAlign: 'center',
            cursor: 'pointer',
            mb: 3,
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 40, mb: 1 }} />
          <Typography>
            {isDragActive
              ? 'Drop the file here...'
              : file
                ? `Selected file: ${file.name}`
                : 'Drag & drop a CSV file here, or click to select'}
          </Typography>
        </Paper>

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || isUploading}
          startIcon={isUploading ? <CircularProgress size={20} /> : null}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>

        {uploadStatus && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1">{uploadStatus}</Typography>
          </Box>
        )}

        {errors.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Validation Errors
            </Typography>
            {errors.map((error, index) => (
              <Alert key={index} severity="error" sx={{ mb: 1 }}>
                {JSON.stringify(error)}
              </Alert>
            ))}
          </Box>
        )}
      </Box>
    </PageLayout>
  );
};
